import AccentButton from "@components/ui/AccentButton";
import { Input } from "@components/ui/input";
import emailjs from "@emailjs/browser";
import { Box, CircularProgress, Container, Paper, Typography } from "@material-ui/core";
import useLanguageStore from "@stores/languageStore";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ContactInfoItem from "../../components/contact/ContactInfoItem";
import SEOHead from "../../components/SEOHead";
import SectionHeader from "../../components/SectionHeader";

const ContactPage = () => {
	const { t } = useTranslation();
	const { language } = useLanguageStore();
	const isRtl = language === "he";
	const formRef = useRef();
	const [isSending, setIsSending] = useState(false);
	const [status, setStatus] = useState({ type: "", message: "" });

	const sendEmail = (e) => {
		e.preventDefault();
		setIsSending(true);
		setStatus({ type: "", message: "" });

		// Replace these with your actual Service ID, Template ID, and Public Key
		// Ideally, use environment variables: import.meta.env.VITE_EMAILJS_SERVICE_ID, etc.
		const serviceID = import.meta.env.VITE_EMAILJS_SERVICE_ID || "YOUR_SERVICE_ID";
		const templateID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
		const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "YOUR_PUBLIC_KEY";

		console.log("Service ID:", serviceID);
		console.log("Template ID:", templateID);
		console.log("Public Key:", publicKey);

		// Remove these logs before deploying to production!

		if (serviceID === "YOUR_SERVICE_ID") {
			console.warn("EmailJS keys are missing. Please set them in your environment variables or code.");
			setStatus({
				type: "error",
				message: t("contact.form.error", "Configuration missing. Please contact administrator."),
			});
			setIsSending(false);
			return;
		}

		emailjs
			.sendForm(serviceID, templateID, formRef.current, {
				publicKey: publicKey,
			})
			.then(
				() => {
					setStatus({
						type: "success",
						message: t("contact.form.success", "Message sent successfully!"),
					});
					formRef.current.reset();
					// Report conversion to Google Ads
					if (window.gtag_report_conversion) {
						window.gtag_report_conversion();
					}
				},
				(error) => {
					console.error("FAILED...", error);
					setStatus({
						type: "error",
						message: t("contact.form.error", "Failed to send message. Please try again."),
					});
				},
			)
			.finally(() => {
				setIsSending(false);
			});
	};

	const contactDetails = [
		{
			label: t("contact.info.phone"),
			value: t("contact.info.phoneValue"),
			isLink: true,
			linkHref: `tel:${t("contact.info.phoneValue")}`,
			onClick: () => {
				if (window.gtag_report_conversion) {
					window.gtag_report_conversion();
				}
			},
		},
		{
			label: t("contact.info.fax"),
			value: t("contact.info.faxValue"),
		},
		{
			label: t("contact.info.email"),
			value: t("contact.info.emailValue"),
			isLink: true,
			linkHref: `mailto:${t("contact.info.emailValue")}`,
		},
	];

	const addressDetails = [
		{
			label: t("contact.info.factory"),
			value: t("contact.info.factoryAddress"),
			isLink: true,
			linkHref: `https://waze.com/ul?q=${encodeURIComponent(t("contact.info.factoryAddress"))}`,
			linkTarget: "_blank",
		},
		{
			label: t("contact.info.office"),
			value: t("contact.info.officeAddress"),
			isLink: true,
			linkHref: `https://waze.com/ul?q=${encodeURIComponent(t("contact.info.officeAddress"))}`,
			linkTarget: "_blank",
		},
	];

	// Helper to extract phone number from strings like "Name: 050-1234567"
	const extractPhone = (str) => {
		const match = str.match(/[\d-]{9,}/);
		return match ? match[0] : null;
	};

	const contact1Text = t("contact.info.contact1");
	const contact2Text = t("contact.info.contact2");
	const contact1Phone = extractPhone(contact1Text);
	const contact2Phone = extractPhone(contact2Text);

	// Helper to format display name separately from phone number
	const formatContactPerson = (text, phone) => {
		if (!phone) return text;
		const namePart = text.replace(phone, "").trim().replace(/:$/, "");
		return (
			<>
				{namePart}:{" "}
				<a
					href={`tel:${phone}`}
					className="hover:text-stone-900 hover:underline"
				>
					{phone}
				</a>
			</>
		);
	};

	const contactInfoItems = [
		{
			label: t("contact.info.contacts"),
			value: (
				<>
					<Typography variant="body2">
						{formatContactPerson(contact1Text, contact1Phone)}
					</Typography>
					<Typography variant="body2">
						{formatContactPerson(contact2Text, contact2Phone)}
					</Typography>
				</>
			),
			isLink: false,
		},
		{
			label: t("contact.info.contactDetails", "Contact details"),
			value: (
				<div>
					{contactDetails.map((detail) => (
						<div
							key={detail.label}
							className={`flex flex-wrap items-baseline gap-x-2 justify-start`}
						>
							<Typography
								variant="body2"
								component="span"
								className="!font-semibold shrink-0"
							>
								{detail.label}:
							</Typography>
							{detail.isLink ? (
								<Typography
									variant="body1"
									component="span"
									className="leading-snug"
								>
									<a
										href={detail.linkHref}
										className="hover:text-stone-900 underline"
										target={detail.linkTarget}
										rel={
											detail.linkTarget === "_blank"
												? "noopener noreferrer"
												: undefined
										}
									>
										{detail.value}
									</a>
								</Typography>
							) : (
								<Typography
									variant="body1"
									component="span"
									className="leading-snug"
								>
									{detail.value}
								</Typography>
							)}
						</div>
					))}
				</div>
			),
			isLink: false,
		},
		{
			label: t("contact.info.website"),
			value: t("contact.info.websiteValue", "www.avnechen.org"),
			isLink: true,
			linkHref: "https://www.avnechen.org",
			linkTarget: "_blank",
		},
		{
			label: t("contact.info.address", "Address"),
			value: (
				<div>
					{addressDetails.map((detail) => (
						<div
							key={detail.label}
							className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 justify-start`}
						>
							<Typography
								variant="body2"
								component="span"
								className="!font-semibold shrink-0"
							>
								{detail.label}:
							</Typography>
							{detail.isLink ? (
								<Typography
									variant="body1"
									component="span"
									className="leading-snug"
								>
									<a
										href={detail.linkHref}
										className="hover:text-stone-900 underline"
										target={detail.linkTarget}
										rel={
											detail.linkTarget === "_blank"
												? "noopener noreferrer"
												: undefined
										}
									>
										{detail.value}
									</a>
								</Typography>
							) : (
								<Typography
									variant="body1"
									component="span"
									className="leading-snug"
								>
									{detail.value}
								</Typography>
							)}
						</div>
					))}
				</div>
			),
		},
	];

	return (
		<div
			className={`min-h-[calc(100vh-64px)] py-20 sm:py-32 max-w-[100vw] overflow-x-hidden ${
				isRtl ? "rtl" : "ltr"
			}`}
		>
			<Container maxWidth="lg" className="px-4 sm:px-8">
				<SEOHead
					title={t("contact.title")}
					description={t("contact.subtitle")}
				/>
				<SectionHeader
					title={t("contact.title")}
					subtitle={t("contact.subtitle")}
					className="mb-12"
					component="h1"
				/>

				<Box className="flex flex-col md:flex-row gap-6">
					<Paper
						className="p-8 shadow-lg h-full flex-1 md:flex-[1]"
						elevation={3}
						style={{ backgroundColor: "#F8F8F8" }}
					>
						<Typography
							variant="h5"
							component="h2"
							className={`mb-6 !font-extrabold ${isRtl ? "text-right" : "text-left"}`}
						>
							{t("contact.info.title")}
						</Typography>
						<Box className={`space-y-4 ${isRtl ? "text-right" : "text-left"}`}>
							{contactInfoItems.map((item) => (
								<ContactInfoItem
									key={item.label}
									label={item.label}
									value={item.value}
									isLink={item.isLink}
									linkHref={item.linkHref}
									linkTarget={item.linkTarget}
									onClick={item.onClick}
									isRtl={isRtl}
								/>
							))}
						</Box>
					</Paper>

					<span className="p-8 bg-white h-full flex-1 md:flex-[2]">
						<form
							ref={formRef}
							onSubmit={sendEmail}
							className="space-y-6"
						>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<Input
									label={t("contact.form.name")}
									name="from_name"
									placeholder={t("contact.form.placeholder.name")}
									dir={isRtl ? "rtl" : "ltr"}
									required
									disabled={isSending}
								/>
								<Input
									label={t("contact.form.phone")}
									type="tel"
									name="phone"
									placeholder={t("contact.form.placeholder.phone")}
									dir={isRtl ? "rtl" : "ltr"}
									required
									disabled={isSending}
								/>
							</div>

							<Input
								as="textarea"
								label={t("contact.form.message")}
								name="message"
								placeholder={t("contact.form.placeholder.message")}
								dir={isRtl ? "rtl" : "ltr"}
								className="min-h-[180px]"
								disabled={isSending}
							/>

							{status.message && (
								<Typography
									variant="body2"
									className={`text-center ${
										status.type === "success" ? "text-green-600" : "text-red-600"
									}`}
								>
									{status.message}
								</Typography>
							)}

							<AccentButton
								type="submit"
								size="large"
								fullWidth
								className="justify-center"
								disabled={isSending}
							>
								{isSending ? <CircularProgress size={24} color="inherit" /> : t("contact.form.send")}
							</AccentButton>
						</form>
					</span>
				</Box>
			</Container>
		</div>
	);
};

export default ContactPage;
